import { formatNoticeDateKey, type NoticeWithCompany } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Group,
  Image,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
} from "@dutyon/ui-kit";
import { Edit, Trash } from "lucide-react";

interface NoticeTableProps {
  notices: NoticeWithCompany[];
  isLoading: boolean;
  onEdit: (notice: NoticeWithCompany) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, status: "DRAFT" | "PUBLISHED") => void;
}

export function NoticeTable({
  notices,
  isLoading,
  onEdit,
  onDelete,
  onToggleStatus,
}: NoticeTableProps) {
  const getStatusBadge = (startDateStr: string | Date, endDateStr: string | Date) => {
    const now = new Date();
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);

    if (now < start) {
      return (
        <Badge color="blue" variant="light">
          대기
        </Badge>
      );
    } else if (start <= now && now <= end) {
      return (
        <Badge color="green" variant="filled">
          노출 중
        </Badge>
      );
    } else {
      return (
        <Badge color="gray" variant="light">
          종료
        </Badge>
      );
    }
  };

  return (
    <Table verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>병원</Table.Th>
          <Table.Th>제목</Table.Th>
          <Table.Th>내용 요약</Table.Th>
          <Table.Th>이미지</Table.Th>
          <Table.Th>배포</Table.Th>
          <Table.Th>노출 상태</Table.Th>
          <Table.Th>게시 기간</Table.Th>
          <Table.Th>등록일</Table.Th>
          <Table.Th>관리</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {isLoading ? (
          <Table.Tr>
            <Table.Td colSpan={9} align="center" py="xl">
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  공지사항 목록을 불러오는 중입니다...
                </Text>
              </Stack>
            </Table.Td>
          </Table.Tr>
        ) : notices.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={9} align="center">
              <Text c="dimmed" p="xl">
                등록된 공지사항이 없습니다.
              </Text>
            </Table.Td>
          </Table.Tr>
        ) : (
          notices.map((notice) => (
            <Table.Tr key={notice.id}>
              <Table.Td fw={600}>
                {notice.companyId ? (
                  notice.company?.name || "-"
                ) : (
                  <Badge color="violet" variant="filled">
                    시스템 전체 공지
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{notice.title}</Table.Td>
              <Table.Td style={{ maxWidth: 300 }}>
                <Text truncate size="sm">
                  {notice.content || "-"}
                </Text>
              </Table.Td>
              <Table.Td>
                {notice.imageUrl ? (
                  <Image
                    src={notice.imageUrl}
                    w={55}
                    h={35}
                    fit="cover"
                    radius="sm"
                    fallbackSrc="https://placehold.co/55x35?text=No+Img"
                  />
                ) : (
                  "-"
                )}
              </Table.Td>
              <Table.Td>
                <Switch
                  checked={notice.status === "PUBLISHED"}
                  onChange={(event) => {
                    const newStatus = event.currentTarget.checked ? "PUBLISHED" : "DRAFT";
                    onToggleStatus(notice.id, newStatus);
                  }}
                />
              </Table.Td>
              <Table.Td>{getStatusBadge(notice.startDate, notice.endDate)}</Table.Td>
              <Table.Td>
                <Text size="sm">
                  {formatNoticeDateKey(notice.startDate)} ~ {formatNoticeDateKey(notice.endDate)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{new Date(notice.createdAt).toLocaleDateString()}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <ActionIcon variant="subtle" color="blue" onClick={() => onEdit(notice)}>
                    <Edit size={16} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => onDelete(notice.id)}>
                    <Trash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))
        )}
      </Table.Tbody>
    </Table>
  );
}
